// Business Admin Dashboard Main Page
import React, { useEffect, useState, useCallback } from "react";
import { useBusinessAdminDashboard } from "@/hooks/business-admin/useBusinessAdminDashboard";
import { useTheme } from "next-themes";
import DashboardMetricCards from "@/components/business-admin/DashboardMetricCards";
import CampaignsTable from "@/components/business-admin/CampaignsTable";

export default function BusinessAdminDashboard() {
  const { theme } = useTheme();
  const {
    overview,
    summary,
    campaigns,
    pagination,
    isLoading,
    isCampaignsLoading,
    filterOptions,
    getDashboardOverview,
    getRecentCampaigns,
    getFilterOptions,
    approveCampaign,
    rejectCampaign,
    updateCampaignStatus,
    editCampaignName,
    deleteCampaign,
  } = useBusinessAdminDashboard();

  const [filters, setFilters] = useState({
    brand: "",
    region: "",
    status: "",
    period: "this_month",
    search: "",
    sort_by: "recent",
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Memoize loadDashboard to prevent unnecessary re-creation
  const loadDashboard = useCallback(async () => {
    await getDashboardOverview({
      brand: filters.brand,
      region: filters.region,
      period: filters.period,
    });
  }, [getDashboardOverview, filters.brand, filters.region, filters.period]);

  // Memoize loadCampaigns to prevent unnecessary re-creation
  const loadCampaigns = useCallback(async () => {
    await getRecentCampaigns({
      page: currentPage,
      per_page: 10,
      brand: filters.brand,
      region: filters.region,
      status: filters.status,
      search: filters.search,
      sort_by: filters.sort_by,
    });
  }, [getRecentCampaigns, currentPage, filters.brand, filters.region, filters.status, filters.search, filters.sort_by]);

  // Load initial data
  useEffect(() => {
    getFilterOptions();
    loadDashboard();
  }, []); // Empty dependency array - only run once on mount

  // Reload campaigns when filters or page change
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApprove = async (campaignId) => {
    try {
      await approveCampaign(campaignId);
      await loadCampaigns();
      await loadDashboard();
    } catch (error) {
      console.error("Failed to approve campaign:", error);
    }
  };

  const handleReject = async (campaignId, reason) => {
    try {
      await rejectCampaign(campaignId, reason);
      await loadCampaigns();
      await loadDashboard();
    } catch (error) {
      console.error("Failed to reject campaign:", error);
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await updateCampaignStatus(campaignId, newStatus);
      await loadCampaigns();
      await loadDashboard();
    } catch (error) {
      console.error("Failed to update campaign status:", error);
    }
  };

  const handleEditCampaignName = async (campaignId, newCampaignName) => {
    try {
      await editCampaignName(campaignId, newCampaignName);
      // Success/error toasts are shown by the hook
      // Refresh data on success
      await loadCampaigns();
      await loadDashboard();
    } catch (error) {
      // Error is already handled and toasted by the hook
      console.error("Failed to edit campaign name:", error);
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await deleteCampaign(campaignId);
      // Success/error toasts are shown by the hook
      // Refresh data on success (only reached if no error thrown)
      await loadCampaigns();
      await loadDashboard();
    } catch (error) {
      // Error is already handled and toasted by the hook
      console.error("Failed to delete campaign:", error);
      // Re-throw to prevent dialog from closing in component
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 pt-24">
      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Metric Cards */}
        <DashboardMetricCards summary={summary} isLoading={isLoading} />

        {/* Campaigns Table */}
        <CampaignsTable
          campaigns={campaigns}
          pagination={pagination}
          isLoading={isCampaignsLoading}
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onApprove={handleApprove}
          onReject={handleReject}
          onStatusChange={handleStatusChange}
          onEditCampaignName={handleEditCampaignName}
          onDelete={handleDelete}
          currentPage={currentPage}
        />
      </main>
    </div>
  );
}
